import Globals from "@/components/globals";
import { assertValidCaddyConfig, assertValidShelfConfig } from "@/utils/validators";
import { COMPONENT_NAME, LADDER_TYPE } from "@/services/component";

function parseComponentTrads(details, names) {
    const { diam_ref1, client_ref1 } = details;

    const trads = { name: {}, diamRef: {}, clientRef: {} };
    // langs may not be limited to EN/FR so we have to make it dynamic
    const languages = Object.keys(names);
    for (const locale of languages) {
        trads.name[locale] = names[locale].value;
        if (diam_ref1 && diam_ref1.componentProperty && diam_ref1.componentProperty.value) {
            trads.diamRef[locale] = diam_ref1.componentProperty.value || "";
        } else {
            trads.diamRef[locale] = "";
        }
        if (client_ref1 && client_ref1.componentProperty && client_ref1.componentProperty.value) {
            trads.clientRef[locale] = client_ref1.componentProperty.value || "";
        } else {
            trads.clientRef[locale] = "";
        }
    }

    return trads;
}

export function parseComponentLabel(component, initialLocale, redirect) {
    const names = component.terms.name,
        details = component.infosArticle,
        type = component.type,
        id = component.id;

    const trads = parseComponentTrads(details, names);

    const res = renderTrads({ names, trads, initialLocale });
    res.root.onclick = function() {
        redirect(`/${type}/${id}`);
    };

    return res;
}

export function renderTrads({ names, trads, initialLocale }) {
    const root = document.createElement("div");
    root.style = "display: flex; flex-direction: column;";

    const name = document.createElement("div");
    const nameStyle = "line-height: 11px;font-size: 11px;text-transform:lowercase;overflow:hidden";
    name.style = nameStyle + "max-height: 22px;";
    root.append(name);

    const diamRef = document.createElement("div");
    diamRef.style = nameStyle + "max-height:11px;font-weight:bold;text-transform:uppercase;";
    root.append(diamRef);

    const clientRef = document.createElement("div");
    clientRef.style = nameStyle + "max-height:11px;font-weight:bold;text-transform:uppercase;";
    root.append(clientRef);

    if (names[initialLocale]) {
        if (typeof names[initialLocale] === "string") {
            name.innerText = names[initialLocale];
        } else {
            name.innerText = names[initialLocale].value;
        }
    }
    if (trads && trads.diamRef && trads.diamRef[initialLocale]) {
        diamRef.innerText = trads.diamRef[initialLocale];
    }
    if (trads && trads.clientRef && trads.clientRef[initialLocale]) {
        clientRef.innerText = trads.clientRef[initialLocale];
    }

    return {
        root,
        name,
        diamRef,
        clientRef,
        trads: {
            name: names,
            ...trads
        }
    };
}

export class ParseComponentError {
    component;
    message;

    constructor(component, message) {
        this.component = component;
        this.message = message;
    }
}

class UnknownComponentTypeError extends ParseComponentError {
    constructor(component) {
        super(component, `unknown component type ${component && component.type}`);
    }
}

class InvalidComponentError extends ParseComponentError {
    constructor(component, err) {
        super(component, err || "missing config property");
    }
}

export function parseImages(component) {
    const figure = Object.values(component.figures_advanced).find(f => f.id_type_file === 6);
    let imageURL;

    if (figure && figure.big) imageURL = `${Globals.getBaseUrl()}${figure.big}`;

    let realist;
    for (const figure of Object.values(component.figures_advanced)) {
        if (figure.id_type_file === 5) {
            realist = `${Globals.getBaseUrl()}${figure.medium}`;
            break;
        }
    }

    return [imageURL, realist];
}

function parseModule(component) {
    if (!component.config) {
        throw new InvalidComponentError(component);
    }
    const width = Number.parseFloat(component.config.width, 10);
    const height = Number.parseFloat(component.config.height, 10);
    if (isNaN(width) || isNaN(height) || width === 0 || height === 0) {
        throw new ParseComponentError(component, "failed to parsed size of");
    }

    const [imageURL, realisticURL] = parseImages(component);

    return () =>
        BoardModuleElement.create(component.id, {
            label: "___",
            width,
            height,
            imageURL,
            realisticURL
        });
}

function parseCaddy(component) {
    assertValidCaddyConfig(component.config);
    return () =>
        BoardCaddy.create(
            component.config.width,
            component.config.height,
            component.config.contentX,
            component.config.contentY,
            component.config.contentWidth,
            component.config.contentHeight,
            component.config.coverHeight,
            component.config.clipDevices,
            component.config.ladderWidth,
            component.config.ladderHeight,
            component.config.topPawl,
            component.config.bottomPawl,
            component.config.pawlInterval,
            component.config.intermediateLaddersCount,
            component.config.leftAdhesive,
            component.config.rightAdhesive,
            component.config.bottomAdhesive
        );
}

export function parseShelf(component) {
    assertValidShelfConfig(component.config);
    return () =>
        BoardShelf.create(
            component.config.width,
            component.config.height,
            component.config.coverHeight,
            component.config.coverY,
            component.config.leftClip,
            component.config.rightClip,
            component.config.labelText,
            component.config.bottomAdhesive
        );
}

function parseFrame(component) {
    console.assert(typeof component.config.color === "string");
    console.assert(typeof component.config.width === "number");
    console.assert(typeof component.config.height === "number");
    console.assert(typeof component.config.thickness === "number");

    return () => BoardFrame.create(component.config.width, component.config.height, component.config.thickness, component.config.color);
}

function parseFascia(component) {
    console.assert(typeof component.config.width === "number");
    console.assert(typeof component.config.height === "number");
    console.assert(typeof component.id === "number");

    return () => BoardImageFascia.create(component.config.width, component.config.height, ...parseImages(component), component.id);
}

function parseBlankingGraphic(component) {
    console.assert(typeof component.config.width === "number");
    console.assert(typeof component.config.height === "number");

    return () => BoardImageBlankingGraphic.create(component.config.width, component.config.height, ...parseImages(component));
}

function parseDivider(component) {
    console.assert(typeof component.config.width === "number");
    console.assert(typeof component.config.height === "number");

    return () => BoardDivider.create(component.config.width, component.config.height);
}

function parseTicketStrip(component) {
    console.assert(typeof component.config.width === "number");
    console.assert(typeof component.config.height === "number");
    console.assert(typeof component.id === "number");
    const images = parseImages(component);

    return () => BoardImageCoverVisual.create(component.config.width, component.config.height, ...images, component.id);
}

export function parseLadder(component) {
    const { width, height, topPawl, bottomPawl, pawlInterval } = component.config;

    switch (component.config.type) {
        case LADDER_TYPE.simple:
            return () => BoardLadder.create(width, height, -height / 2 + topPawl, height / 2 - bottomPawl, pawlInterval);
        case LADDER_TYPE.double:
            return () => BoardDoubleLadder.create(width, height, -height / 2 + topPawl, height / 2 - bottomPawl, pawlInterval);
        default:
            throw new InvalidComponentError(component);
    }
}

function parseComponentBuilder(component) {
    if (!component.config) {
        throw new InvalidComponentError(component);
    }

    try {
        switch (component.type) {
            case COMPONENT_NAME.module:
                return parseModule(component);
            case COMPONENT_NAME.shelf:
                return parseShelf(component);
            case COMPONENT_NAME.caddy:
                return parseCaddy(component);
            case COMPONENT_NAME.fascia:
                return parseFascia(component);
            case COMPONENT_NAME.blankingGraphic:
                return parseBlankingGraphic(component);
            case COMPONENT_NAME.frame:
                return parseFrame(component);
            case COMPONENT_NAME.divider:
                return parseDivider(component);
            case COMPONENT_NAME.ticketStrip:
                return parseTicketStrip(component);
            case COMPONENT_NAME.ladder:
                return parseLadder(component);
            default:
                throw new UnknownComponentTypeError(component);
        }
    } catch (err) {
        throw new InvalidComponentError(component, err);
    }
}

function parseComponentImage(component) {
    for (const figure of Object.values(component.figures_advanced)) {
        if (figure.id_type_file === 1) {
            return `${Globals.getBaseUrl()}${figure.medium}`;
        }
    }
}

export function parseComponent(component, initialLocale, redirect) {
    return {
        label: parseComponentLabel(component, initialLocale, redirect),
        builder: parseComponentBuilder(component),
        image: parseComponentImage(component),
        data: component,
        id: component.id
    };
}



// WEBPACK FOOTER //
// ./src/plano/utils/parse-catalog.js